import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PlanModal from './PlanModal';
import { FetchPlans, DeletePlan, plansSelector, totalCountSelector, loadingSelector } from './SubscriptionSlice';

const PlansList = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const plans = useSelector(plansSelector);
  const total = useSelector(totalCountSelector);
  const loading = useSelector(loadingSelector);

  useEffect(() => {
    loadPlans();
  }, [currentPage, searchTerm]);

  const loadPlans = () => {
    dispatch(FetchPlans({ page: currentPage, limit, search: searchTerm }));
  };

  const handleCreate = () => {
    setSelectedPlan(null);
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      const result = await dispatch(DeletePlan(id));
      if (result.payload?.code === 1) {
        toast.success('Plan deleted successfully');
        loadPlans();
      } else {
        toast.error('Failed to delete plan');
      }
    }
  };

  const handleModalClose = (refresh) => {
    setShowModal(false);
    setSelectedPlan(null);
    if (refresh) {
      loadPlans();
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="row align-items-center">
        <div className="col-sm-12 col-md-6 col-lg-6"></div>
        <div className="col-sm-12 col-md-6 col-lg-6">
          <div className="d-flex align-items-center lg-justify-content-end mb-3">
            <form className="has-search">
              <span className="form-search-icon form-control-feedback"></span>
              <input
                type="text"
                className="form-control shadow"
                placeholder="Search plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </form>
            <span onClick={handleCreate} className="" style={{ cursor: 'pointer' }}>
              <img
                src="assets/images/addBtn.png"
                alt=""
                className="add-new"
              />
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="table-responsive text-center">
            <table className="table storetable">
              <thead className="text-center">
                <tr>
                  <th scope="col">Plan Name</th>
                  <th scope="col">Description</th>
                  <th scope="col">Price</th>
                  <th scope="col">Student Limit</th>
                  <th scope="col">Plan Type</th>
                  <th scope="col">Stripe Price ID</th>
                  <th scope="col">Action</th>
                </tr>
                <tr className="border-0 bg-none invisible">
                  <td className="border-0 bg-none"></td>
                </tr>
              </thead>
              <tbody>
                {plans && plans.length > 0 ? (
                  plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>{plan.plan_name}</td>
                      <td>{plan.description || '-'}</td>
                      <td>£{parseFloat(plan.price).toFixed(2)}</td>
                      <td>{plan.student_limit}</td>
                      <td>
                        {plan.plan_type === 'custom' && plan.company ? (
                          <span style={{ color: '#6610f2', fontWeight: '500' }}>{plan.company.company_name}</span>
                        ) : (
                          <span style={{ color: '#198754', fontWeight: '500' }}>General</span>
                        )}
                      </td>
                      <td><small>{plan.stripe_price_id || '-'}</small></td>
                      <td>
                        <div className="d-flex justify-content-center action align-items-center">
                          <button
                            className="btn"
                            onClick={() => handleEdit(plan)}
                            title="Edit Plan"
                          >
                            <img
                              src="assets/images/edit.png"
                              alt=""
                            />
                          </button>
                          <button
                            className="btn"
                            onClick={() => handleDelete(plan.id)}
                            title="Delete Plan"
                          >
                            <img
                              src="assets/images/delete.png"
                              alt=""
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No plans found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, index) => (
                    <li
                      key={index}
                      className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {showModal && (
        <PlanModal
          show={showModal}
          onClose={handleModalClose}
          plan={selectedPlan}
        />
      )}
    </div>
  );
};

export default PlansList;
